// Cash Flow Forecast for next month — CONSERVATIVE.
// Uses ONLY recurring `income` (salary, rent). Excludes one-time `revenue` and `borrow`
// because they aren't guaranteed to repeat next month.
import { useMemo } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useInstallments } from "@/lib/useInstallmentsCache";
import { useCommitments } from "@/lib/useCommitmentsCache";

function fmt(n, ccy) {
  return `${Math.round(Number(n || 0)).toLocaleString()} ${ccy}`;
}

export function NeuCashFlowForecast({ summary }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const data = useInstallments();
  const cmt = useCommitments();
  const ccy = user?.currency || "AED";

  const { income, revenue, commitments, avgDaily, forecast, positive } = useMemo(() => {
    const tm = summary?.this_month || {};
    const income = tm.income || 0;
    const revenue = tm.revenue || 0;
    const commitments = (data?.total_monthly || 0) + (cmt?.total_monthly || 0);
    const days = summary?.daily || [];
    const totalSpend = days.reduce((a, d) => a + (d.amount || 0), 0);
    const avgDaily = days.length > 0 ? totalSpend / days.length : 0;
    const nextMonthSpend = avgDaily * 30;
    // Forecast uses ONLY recurring income — revenue is NOT projected.
    const forecast = income - commitments - nextMonthSpend;
    return { income, revenue, commitments, avgDaily, forecast, positive: forecast >= 0 };
  }, [summary, data, cmt]);

  const tone = positive ? "text-emerald-600" : "text-red-600";

  return (
    <div data-testid="cashflow-forecast" className="neu p-4 sm:p-5 space-y-3">
      <div className="min-w-0">
        <p className="font-display text-lg font-bold neu-accent-blue">{t("cashFlowForecast")}</p>
        <p className={`font-display font-extrabold text-lg ${tone}`}>
          {positive ? "+" : ""}{fmt(forecast, ccy)}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] tabular-nums">
        <div className="text-center"><p className="text-slate-500">{t("income")}</p><p className="font-bold text-emerald-600">{fmt(income, ccy)}</p></div>
        <div className="text-center"><p className="text-slate-500">{t("commitments")}</p><p className="font-bold text-indigo-600">−{fmt(commitments, ccy)}</p></div>
        <div className="text-center"><p className="text-slate-500">{t("dailyExpected")}</p><p className="font-bold text-amber-600">−{fmt(avgDaily * 30, ccy)}</p></div>
      </div>
      {revenue > 0 && (
        <p className="text-[10px] text-sky-600 font-semibold text-center">
          {t("revenue")}: +{fmt(revenue, ccy)} · {t("revenueHint")}
        </p>
      )}
      {!positive && (
        <p className="text-[11px] text-red-600 font-semibold">{t("forecastNegativeAdvice")}</p>
      )}
    </div>
  );
}
